import { formatCategoryPlural } from './format';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface PricingRow {
  item: string;
  price: string;
}

export interface SeoContentSection {
  title: string;
  subtitle?: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface CategoryCitySeoPayload {
  h1Title: string;
  h2Subheading: string;
  introParagraph: string;
  sections: SeoContentSection[];
  faqs: FaqItem[];
  pricingTable?: { title: string; rows: PricingRow[] };
  statutoryNotice?: string;
}

export type CategoryArchetype =
  | 'RETAIL_PRODUCT'
  | 'ONDEMAND_SERVICE'
  | 'APPOINTMENT_SERVICE'
  | 'HOSPITALITY'
  | 'PROFESSIONAL_ADVISORY'
  | 'HIGH_CONSIDERATION';

export function getCategoryArchetype(categoryRaw: string): CategoryArchetype {
  const c = (categoryRaw || '').toLowerCase();
  if (/wine|liquor|alcohol|beer|bakery|pharmacy|hardware|mobile|stationery|grocery|supermarket|clothing|shop|store|textiles|printer|dealer|supplier|trader|distributor|equipment|art|painting|paintings|canvas/.test(c)) {
    return 'RETAIL_PRODUCT';
  }
  if (/plumber|electrician|ac repair|pest control|packers|welder|carpenter|cleaning|laundry|painter|appliance/.test(c)) {
    return 'ONDEMAND_SERVICE';
  }
  if (/dentist|salon|clinic|doctor|hospital|physio|vet|diagnostics|spa|beauty|dermatologist|pathology|health|healthcare|medical|nursing|maternity/.test(c)) {
    return 'APPOINTMENT_SERVICE';
  }
  if (/restaurant|cafe|hotel|banquet|bar|pub|catering|resort|dining|bistro/.test(c)) {
    return 'HOSPITALITY';
  }
  if (/ca|lawyer|advocate|architect|consultant|agency|accounting|tax|auditor|corporate/.test(c)) {
    return 'PROFESSIONAL_ADVISORY';
  }
  return 'HIGH_CONSIDERATION'; // Real estate, Gym, College, Car dealer, etc.
}

export function getCategoryPricingTable(categoryRaw: string, categoryPlural: string, city?: string): { title: string; rows: PricingRow[] } | undefined {
  const c = (categoryRaw || '').toLowerCase();

  // AC Dealers
  if (c.includes('ac dealer') || c.includes('air condition')) {
    return { title: city ? `Cost of AC Provided by ${categoryPlural} in ${city}` : `Cost of AC Provided by ${categoryPlural}`, rows: [
      { item: 'Window AC (1 Ton)', price: '₹19,500 per piece onwards' },
      { item: 'Split AC with Indoor Unit (1 Ton)', price: '₹28,650 per piece onwards' },
      { item: 'Split AC with Indoor Unit (1.5 Ton)', price: '₹34,990 per piece onwards' },
      { item: 'Cassette AC (2 Ton)', price: '₹65,000 per piece onwards' },
      { item: 'Installation Charges', price: '₹1,500 – ₹3,500 per unit' },
    ]};
  }
  // Plumbers
  if (c.includes('plumb')) {
    return { title: city ? `Plumbing Service Charges by ${categoryPlural} in ${city}` : `Plumbing Service Charges by ${categoryPlural}`, rows: [
      { item: 'Tap Repair / Replacement', price: '₹200 – ₹800 per job' },
      { item: 'Pipe Leakage Fixing', price: '₹500 – ₹2,000 per job' },
      { item: 'Bathroom Fitting Installation', price: '₹1,500 – ₹5,000 per job' },
      { item: 'Overhead Tank Cleaning', price: '₹800 – ₹2,500 per job' },
      { item: 'Full Bathroom Plumbing Setup', price: '₹8,000 – ₹25,000 per bathroom' },
    ]};
  }
  // Electricians
  if (c.includes('electric')) {
    return { title: city ? `Electrician Service Rates in ${city}` : `Electrician Service Rates`, rows: [
      { item: 'Fan / Light Fitting', price: '₹200 – ₹500 per point' },
      { item: 'Wiring (per room)', price: '₹1,500 – ₹5,000' },
      { item: 'MCB / DB Box Replacement', price: '₹500 – ₹2,000' },
      { item: 'Inverter / UPS Installation', price: '₹800 – ₹2,500' },
      { item: 'Full House Wiring (2BHK)', price: '₹15,000 – ₹40,000' },
    ]};
  }
  // Painters
  if (c.includes('paint')) {
    return { title: city ? `Painting Service Cost Breakdown in ${city}` : `Painting Service Cost Breakdown`, rows: [
      { item: 'Interior Painting (per sq. ft)', price: '₹12 – ₹25 per sq. ft' },
      { item: 'Exterior Painting (per sq. ft)', price: '₹18 – ₹35 per sq. ft' },
      { item: 'Texture / Stucco Finish', price: '₹40 – ₹80 per sq. ft' },
      { item: '2BHK Full Interior Painting', price: '₹18,000 – ₹35,000' },
      { item: '3BHK Full Interior Painting', price: '₹28,000 – ₹55,000' },
    ]};
  }
  // Carpenters
  if (c.includes('carpenter') || c.includes('furniture')) {
    return { title: city ? `Carpentry & Furniture Work Pricing in ${city}` : `Carpentry & Furniture Work Pricing`, rows: [
      { item: 'Modular Kitchen (per sq. ft)', price: '₹800 – ₹2,000 per sq. ft' },
      { item: 'Wardrobe (per door)', price: '₹8,000 – ₹20,000' },
      { item: 'False Ceiling (per sq. ft)', price: '₹60 – ₹150 per sq. ft' },
      { item: 'Door Frame & Fitting', price: '₹2,500 – ₹6,000' },
      { item: 'Furniture Repair', price: '₹500 – ₹3,000 per item' },
    ]};
  }
  // Interior Designers
  if (c.includes('interior')) {
    return { title: city ? `Interior Design Cost Estimate in ${city}` : `Interior Design Cost Estimate`, rows: [
      { item: '1BHK Full Interior Design', price: '₹2.5 Lakh – ₹6 Lakh' },
      { item: '2BHK Full Interior Design', price: '₹4 Lakh – ₹10 Lakh' },
      { item: '3BHK Full Interior Design', price: '₹6 Lakh – ₹18 Lakh' },
      { item: 'Office Interior (per sq. ft)', price: '₹500 – ₹1,500 per sq. ft' },
      { item: 'Design Consultation Fee', price: '₹2,000 – ₹10,000 (refundable)' },
    ]};
  }
  // Pest Control
  if (c.includes('pest')) {
    return { title: city ? `Pest Control Service Charges in ${city}` : `Pest Control Service Charges`, rows: [
      { item: 'Cockroach Treatment (1BHK)', price: '₹800 – ₹1,500' },
      { item: 'Termite Treatment (per sq. ft)', price: '₹5 – ₹15 per sq. ft' },
      { item: 'Bed Bug Treatment', price: '₹1,500 – ₹4,000' },
      { item: 'Rodent Control', price: '₹1,000 – ₹3,000' },
      { item: 'General Pest Control (3BHK)', price: '₹1,500 – ₹3,000' },
    ]};
  }
  // Restaurants
  if (c.includes('restaurant') || c.includes('dining')) {
    return { title: city ? `Dining Cost & Average Meal Prices in ${city}` : `Dining Cost & Average Meal Prices`, rows: [
      { item: 'Lunch (per person)', price: '₹150 – ₹500' },
      { item: 'Dinner (per person)', price: '₹200 – ₹800' },
      { item: 'Family Meal (4 persons)', price: '₹800 – ₹2,500' },
      { item: 'Private Dining / Event (min. spend)', price: '₹5,000 – ₹25,000' },
      { item: 'Home Delivery Order (min. order)', price: '₹199 – ₹499' },
    ]};
  }
  // Hotels
  if (c.includes('hotel') || c.includes('resort')) {
    return { title: city ? `Hotel Room Tariff Overview in ${city}` : `Hotel Room Tariff Overview`, rows: [
      { item: 'Budget / Economy Room (per night)', price: '₹1,200 – ₹2,500' },
      { item: 'Standard Room (per night)', price: '₹2,500 – ₹5,000' },
      { item: 'Deluxe Room (per night)', price: '₹5,000 – ₹12,000' },
      { item: 'Suite / Premium Room (per night)', price: '₹12,000 – ₹40,000' },
      { item: 'Banquet Hall Booking (per day)', price: '₹25,000 – ₹2,00,000' },
    ]};
  }
  // Packers & Movers
  if (c.includes('packer') || c.includes('mover')) {
    return { title: city ? `Packers & Movers Cost Estimate in ${city}` : `Packers & Movers Cost Estimate`, rows: [
      { item: '1BHK Local Shifting (within city)', price: '₹3,000 – ₹8,000' },
      { item: '2BHK Local Shifting', price: '₹5,000 – ₹12,000' },
      { item: '3BHK Local Shifting', price: '₹8,000 – ₹18,000' },
      { item: 'Intercity Shifting (per km)', price: '₹25 – ₹60 per km' },
      { item: 'Car Transport (per 1000 km)', price: '₹6,000 – ₹15,000' },
    ]};
  }
  // Gyms
  if (c.includes('gym') || c.includes('fitness')) {
    return { title: city ? `Gym Membership Fee Structure in ${city}` : `Gym Membership Fee Structure`, rows: [
      { item: 'Monthly Membership', price: '₹800 – ₹2,500 per month' },
      { item: 'Quarterly Membership', price: '₹2,000 – ₹6,500' },
      { item: 'Half-Yearly Membership', price: '₹3,500 – ₹11,000' },
      { item: 'Annual Membership', price: '₹6,000 – ₹20,000' },
      { item: 'Personal Training Session', price: '₹500 – ₹2,000 per session' },
    ]};
  }
  // Salons / Beauty
  if (c.includes('salon') || c.includes('beauty') || c.includes('spa')) {
    return { title: city ? `Salon & Spa Service Charges in ${city}` : `Salon & Spa Service Charges`, rows: [
      { item: 'Haircut (Men)', price: '₹100 – ₹500' },
      { item: 'Haircut + Styling (Women)', price: '₹300 – ₹1,500' },
      { item: 'Facial / Cleanup', price: '₹500 – ₹3,000' },
      { item: 'Full Body Massage (60 min)', price: '₹1,500 – ₹5,000' },
      { item: 'Bridal Makeup Package', price: '₹8,000 – ₹50,000' },
    ]};
  }
  // Real Estate
  if (c.includes('real estate') || c.includes('property')) {
    return { title: city ? `Property Price Range Overview in ${city}` : `Property Price Range Overview`, rows: [
      { item: '1BHK Apartment (purchase)', price: '₹25 Lakh – ₹60 Lakh' },
      { item: '2BHK Apartment (purchase)', price: '₹45 Lakh – ₹1.2 Crore' },
      { item: '3BHK Apartment (purchase)', price: '₹75 Lakh – ₹2.5 Crore' },
      { item: 'Independent Villa', price: '₹80 Lakh – ₹5 Crore+' },
      { item: 'Broker Commission', price: '1% – 2% of property value' },
    ]};
  }
  // Healthcare / Doctors
  if (c.includes('doctor') || c.includes('clinic') || c.includes('hospital') || c.includes('health')) {
    return { title: city ? `Medical Consultation Fee in ${city} Reference` : `Medical Consultation Fee Reference`, rows: [
      { item: 'General Physician Consultation', price: '₹300 – ₹800' },
      { item: 'Specialist Doctor Consultation', price: '₹500 – ₹2,000' },
      { item: 'Dental Checkup', price: '₹200 – ₹500' },
      { item: 'Blood Test Panel (basic)', price: '₹400 – ₹1,500' },
      { item: 'ECG / X-Ray', price: '₹300 – ₹800' },
    ]};
  }
  // Car Repair / Service
  if (c.includes('car service') || c.includes('garage') || c.includes('mechanic') || c.includes('auto')) {
    return { title: city ? `Car Service & Repair Cost Guide in ${city}` : `Car Service & Repair Cost Guide`, rows: [
      { item: 'General Service (Petrol car)', price: '₹2,500 – ₹6,000' },
      { item: 'General Service (Diesel car)', price: '₹3,500 – ₹8,000' },
      { item: 'Denting & Painting (per panel)', price: '₹1,500 – ₹5,000' },
      { item: 'AC Gas Refilling', price: '₹1,500 – ₹3,500' },
      { item: 'Full Car Detailing', price: '₹3,000 – ₹10,000' },
    ]};
  }

  // Generic fallback pricing table for any other category
  return {
    title: city ? `Service Pricing & Cost Estimate for ${categoryPlural} in ${city}` : `Service Pricing & Cost Estimate for ${categoryPlural}`,
    rows: [
      { item: `Basic Service / Consultation Charge`, price: '₹300 – ₹1,000' },
      { item: `Standard Service Package`, price: '₹1,500 – ₹5,000' },
      { item: `Premium / Complete Package`, price: '₹5,000 – ₹20,000' },
      { item: `Custom Bulk / Specialized Work`, price: 'On Request / Written Quote' },
    ]
  };
}

export function getRegulatoryTerm(categoryRaw: string): string {
  const c = (categoryRaw || '').toLowerCase();
  if (c.includes('real estate') || c.includes('property') || c.includes('properties')) {
    return 'RERA Registration';
  }
  if (c.includes('edu') || c.includes('school') || c.includes('college') || c.includes('university') || c.includes('coaching')) {
    return 'AICTE / UGC Accreditation';
  }
  if (c.includes('health') || c.includes('doctor') || c.includes('clinic') || c.includes('hospital') || c.includes('dental')) {
    return 'Medical Council & NABH Certification';
  }
  if (c.includes('wine') || c.includes('liquor') || c.includes('bar') || c.includes('pub')) {
    return 'State Excise License & FSSAI Registration';
  }
  if (c.includes('restaurant') || c.includes('cafe') || c.includes('food') || c.includes('bakery')) {
    return 'FSSAI Food Safety License';
  }
  return 'GSTIN & Commercial Trade License';
}

export function getSeoContentForCategoryAndCity(
  categoryRaw: string,
  cityRaw: string
): CategoryCitySeoPayload {
  const categoryPlural = formatCategoryPlural(categoryRaw || 'Services');
  const city = cityRaw || 'Chennai';
  const archetype = getCategoryArchetype(categoryRaw);
  const regTerm = getRegulatoryTerm(categoryRaw);

  // Statutory Monopoly Guard (e.g. Tamil Nadu Prohibition Act / TASMAC)
  const isAlcohol = /wine|liquor|alcohol|beer/i.test(categoryRaw);
  const isTN = /chennai|coimbatore|madurai|tiruchirappalli|salem|tiruppur|tn/i.test(city);
  let statutoryNotice: string | undefined;

  if (isAlcohol && isTN) {
    statutoryNotice = `Notice: In Tamil Nadu, retail liquor vending is under statutory state regulation via TASMAC outlets. Private retail wine shop licenses are not issued. Outlets listed are licensed hospitality venues or state-regulated premises.`;
  }

  let h1Title = '';
  let h2Subheading = '';
  let introParagraph = '';
  let sections: SeoContentSection[] = [];
  let faqs: FaqItem[] = [];

  switch (archetype) {
    case 'RETAIL_PRODUCT':
      h1Title = `Find Verified ${categoryPlural} in ${city}`;
      h2Subheading = `Local Directory, Store Locations & Retail Stock Outlets for ${categoryPlural} in ${city}`;
      introParagraph = `Looking to purchase products or engage specialists for ${categoryPlural.toLowerCase()} in ${city}? Zha Search aggregates verified retail outlets, showroom addresses, operating hours, direct phone numbers, and location maps. Compare product varieties, pricing ranges, and customer feedback across leading vendors in ${city}.`;
      sections = [
        {
          title: `Comprehensive Buyer's Guide for ${categoryPlural} in ${city}`,
          paragraphs: [
            `Purchasing products from certified ${categoryPlural.toLowerCase()} in ${city} guarantees authentic quality, manufacturer backing, transparent billing, and reliable customer service. Whether you require immediate stock availability or custom orders, local vendors in ${city} provide direct consultation and doorstep delivery options.`
          ],
          bullets: [
            `Store Operating Hours: Most retail establishments operate from 9:30 AM to 9:00 PM throughout the week.`,
            `Quality & Authenticity Checks: Inspect official tax GSTIN receipts, holographic security tags, and manufacturer certificates.`,
            `Flexible Payment Options: Cash, UPI (Google Pay, PhonePe, Paytm), and major Credit/Debit card payments are widely accepted.`,
            `Direct Location Navigation: Utilize Zha Search's direct Google Maps integration to reach store premises effortlessly.`
          ]
        },
        {
          title: `Pricing Range, Warranty & Verification Standards in ${city}`,
          paragraphs: [
            `Price points for ${categoryPlural.toLowerCase()} in ${city} vary depending on brand reputation, material specifications, and warranty coverage. Reputable dealers provide itemized tax invoices with printed MRP details to prevent overcharging. Always request written warranty documents and return policy terms before completing high-value purchases.`
          ]
        },
        {
          title: `Best Time to Visit & Delivery Timelines in ${city}`,
          paragraphs: [
            `Retail stores and showrooms across ${city} experience higher customer footfall during evening hours and weekends. Visiting during morning hours guarantees personalized assistance from store representatives. For online or phone inquiries, most sellers offer same-day or next-day local delivery across major postal zones.`
          ]
        }
      ];
      faqs = [
        {
          question: `What are the typical store operating hours for ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Most stores open between 9:00 AM and 10:00 AM and close between 8:30 PM and 9:30 PM. Call the seller directly on Zha Search to confirm holiday hours.`
        },
        {
          question: `How can I verify the authenticity of ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Check for official business registration (${regTerm}), tax invoices printed with GSTIN details, and verified user ratings on Zha Search.`
        },
        {
          question: `Do dealers of ${categoryPlural.toLowerCase()} in ${city} offer home delivery?`,
          answer: `Yes, many verified sellers in ${city} provide local doorstep delivery within 24 to 48 hours of order confirmation.`
        },
        {
          question: `What payment options are supported by vendors in ${city}?`,
          answer: `Verified outlets accept cash, UPI transfers (GPay/PhonePe), card payments, and corporate bank billing.`
        }
      ];
      break;

    case 'ONDEMAND_SERVICE':
      h1Title = `Book Reliable ${categoryPlural} in ${city}`;
      h2Subheading = `On-Demand Response, Doorstep Inspection & Emergency Technicians in ${city}`;
      introParagraph = `Need urgent professional assistance from qualified ${categoryPlural.toLowerCase()} in ${city}? Zha Search connects home owners, businesses, and property managers directly with verified technicians for doorstep diagnostics, routine maintenance, and emergency service calls across ${city}.`;
      sections = [
        {
          title: `Doorstep Response & Service Booking Checklist for ${categoryPlural} in ${city}`,
          paragraphs: [
            `Hiring experienced ${categoryPlural.toLowerCase()} in ${city} ensures efficient troubleshooting, genuine spare replacement, and long-term durability. Professional technicians carry specialized diagnostic toolkits and offer transparent callout estimates before beginning work.`
          ],
          bullets: [
            `Doorstep Diagnostics: Technicians perform on-site evaluation before providing a final repair estimate.`,
            `Technician ID & Safety Verification: Ensure service personnel present official company ID cards prior to home entry.`,
            `Transparent Callout Fees: Initial inspection charges are typically offset against total repair bills upon approval.`,
            `Emergency Callout Access: Direct phone connect enables instant dispatch for critical maintenance needs.`
          ]
        },
        {
          title: `Service Pricing Models & Labor Costs in ${city}`,
          paragraphs: [
            `Service costs for ${categoryPlural.toLowerCase()} in ${city} depend on job complexity, replacement component costs, and urgency. Standard maintenance visits follow fixed service rate cards, while complex overhauls receive custom written quotes.`
          ]
        },
        {
          title: `Safety Standards, Component Warranties & Service Guarantees in ${city}`,
          paragraphs: [
            `Top service agencies in ${city} provide a 30-day service warranty on labor and manufacturer warranties on all replaced spare parts. Always retain your digital service receipt for warranty claims.`
          ]
        }
      ];
      faqs = [
        {
          question: `How fast can a technician for ${categoryPlural.toLowerCase()} visit my location in ${city}?`,
          answer: `Most verified service providers on Zha Search offer same-day doorstep visits within 2 to 4 hours of your call.`
        },
        {
          question: `What are the callout inspection charges for ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Basic inspection fees range between ₹150 to ₹350, which is adjusted against the overall service charge when work is approved.`
        },
        {
          question: `Are spare parts included in the service warranty in ${city}?`,
          answer: `Yes, genuine replacement spares come with manufacturer warranty cards provided by the service partner.`
        }
      ];
      break;

    case 'APPOINTMENT_SERVICE':
      h1Title = `Consult Top-Rated ${categoryPlural} in ${city}`;
      h2Subheading = `Verified Appointments, Specialist Profiles & Clinic Directory in ${city}`;
      introParagraph = `Schedule consultations with accredited ${categoryPlural.toLowerCase()} in ${city}. Compare professional qualifications, clinic locations, operating schedules, consultation fees, and patient reviews on Zha Search.`;
      sections = [
        {
          title: `Consultation Guide & Professional Qualifications in ${city}`,
          paragraphs: [
            `Choosing certified professionals for ${categoryPlural.toLowerCase()} in ${city} ensures clinical excellence, hygiene safety, and ethical care. Verified practitioners maintain updated state council registrations and modern facility infrastructure.`
          ],
          bullets: [
            `Professional Certification: Verify state council registration numbers (${regTerm}).`,
            `Prior Appointment Scheduling: Booking in advance reduces clinic waiting time significantly.`,
            `Sanitization & Hygiene Standards: Top centers adhere to strict sterilisation and safety protocols.`,
            `Patient Record Privacy: Consultation histories and diagnostics are stored securely.`
          ]
        },
        {
          title: `Clinic Facilities, Equipment & Patient Comfort in ${city}`,
          paragraphs: [
            `Leading facilities for ${categoryPlural.toLowerCase()} in ${city} are equipped with advanced diagnostic machinery, comfortable waiting lounges, and dedicated support staff to assist patients throughout their visit.`
          ]
        },
        {
          title: `Consultation Fee Structures & Payment Policies in ${city}`,
          paragraphs: [
            `Consultation fees across ${city} vary based on practitioner seniority and facility tier. First-time consultation fees typically cover initial examination and prescription guidance.`
          ]
        }
      ];
      faqs = [
        {
          question: `Do I need to book a prior appointment for ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Yes, booking in advance via direct phone numbers on Zha Search guarantees your preferred slot and avoids long waiting times.`
        },
        {
          question: `How can I verify practitioner credentials in ${city}?`,
          answer: `Check professional council registration numbers (${regTerm}) displayed at the facility or on Zha Search verification badges.`
        }
      ];
      break;

    case 'HOSPITALITY':
      h1Title = `Explore Top ${categoryPlural} in ${city}`;
      h2Subheading = `Dining Venues, Hospitality Outlets & Event Spaces in ${city}`;
      introParagraph = `Discover top ${categoryPlural.toLowerCase()} in ${city}. Explore venue ambience, seating capacities, operating hours, food safety FSSAI licenses, customer ratings, and direct contact numbers for table reservations on Zha Search.`;
      sections = [
        {
          title: `Dining Ambience, Specialties & Experience in ${city}`,
          paragraphs: [
            `Hospitality establishments across ${city} cater to diverse dining preferences, family gatherings, corporate celebrations, and casual outings. Premier venues maintain pristine kitchen hygiene and curated specialty menus.`
          ],
          bullets: [
            `Menu Varieties: Explore multi-cuisine, vegetarian, non-vegetarian, and chef special selections.`,
            `Advance Table Reservations: Reserve tables prior to weekend peak hours to guarantee seating.`,
            `Food Safety Badges: Outlets hold valid FSSAI hygiene ratings and government health clearances.`,
            `Event & Party Catering: Many venues offer private dining rooms and party package deals.`
          ]
        },
        {
          title: `Peak Hours, Weekend Reservations & Parking in ${city}`,
          paragraphs: [
            `Popular venues in ${city} experience maximum footfall between 7:30 PM and 10:30 PM on weekends. Reserving your table through direct call access ensures hassle-free entry and valet parking support.`
          ]
        }
      ];
      faqs = [
        {
          question: `Are advance reservations required for ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Yes, weekend dining and group celebrations require advance table booking by calling the venue directly.`
        },
        {
          question: `What are the typical operating hours for venues in ${city}?`,
          answer: `Most establishments open at 11:00 AM and close between 11:00 PM and midnight.`
        }
      ];
      break;

    case 'PROFESSIONAL_ADVISORY':
      h1Title = `Consult Expert ${categoryPlural} in ${city}`;
      h2Subheading = `Certified Corporate Advisors, Compliance Services & Legal Consultants in ${city}`;
      introParagraph = `Engage experienced ${categoryPlural.toLowerCase()} in ${city} for legal advisory, financial compliance, corporate auditing, tax planning, and strategic consulting. Compare credentials, council memberships, firm locations, and verified track records on Zha Search.`;
      sections = [
        {
          title: `Scope of Work & Engagement Models in ${city}`,
          paragraphs: [
            `Professional advisory firms in ${city} provide structured consulting models tailored for individuals, startups, and corporate enterprises. Experienced advisors assist with document drafting, statutory filings, and official representations.`
          ],
          bullets: [
            `Statutory Compliance: Management of regulatory filings, GST returns, and legal documentation.`,
            `Transparent Fee Letters: Clear engagement letters detailing project scope, timelines, and deliverables.`,
            `Confidentiality Agreements: Strict data security and client privacy non-disclosure guarantees.`,
            `End-to-End Execution: Complete advisory support from initial audit to final government filing.`
          ]
        },
        {
          title: `Retainer Plans & Billing Structure in ${city}`,
          paragraphs: [
            `Advisory professionals in ${city} offer flexible fee options including annual retainer contracts, project-based flat fees, and hourly consultation rates. Request a preliminary consultation to align deliverables.`
          ]
        }
      ];
      faqs = [
        {
          question: `How do I verify the credentials of advisory firms in ${city}?`,
          answer: `Verify statutory council memberships (${regTerm}), ICAI/Bar Council registration numbers, and client testimonials.`
        },
        {
          question: `What documentation is needed for an initial consultation in ${city}?`,
          answer: `Bring your relevant business tax records, identity documents, and prior filing history for a precise evaluation.`
        }
      ];
      break;

    case 'HIGH_CONSIDERATION':
    default:
      h1Title = `Compare Verified ${categoryPlural} in ${city}`;
      h2Subheading = `Comprehensive Directory, Verification Details & Market Comparison for ${categoryPlural} in ${city}`;
      introParagraph = `Evaluate top ${categoryPlural.toLowerCase()} in ${city}. Compare locations, verification credentials (${regTerm}), pricing structures, and direct contact numbers on Zha Search. Make well-informed decisions backed by verified data and community reviews.`;
      sections = [
        {
          title: `High-Consideration Selection & Verification Guide in ${city}`,
          paragraphs: [
            `Selecting reliable ${categoryPlural.toLowerCase()} in ${city} requires evaluating statutory registration approvals, market reputation, client history, and transparent financial terms. Zha Search streamlines your research by listing verified contact details and location maps.`
          ],
          bullets: [
            `Statutory Licensing: Verify valid business registration credentials (${regTerm}).`,
            `Transparent Pricing: Compare detailed quotations and payment milestone schedules.`,
            `On-Site Inspections: Schedule in-person visits to evaluate facilities and infrastructure.`,
            `Verified Customer Reviews: Read authentic community feedback before making commitments.`
          ]
        },
        {
          title: `Market Price Comparisons & Financial Planning in ${city}`,
          paragraphs: [
            `High-consideration decisions in ${city} involve significant capital outlay. Requesting written estimates from multiple verified providers allows you to compare competitive rates and negotiate favorable terms.`
          ]
        },
        {
          title: `Verification Checklist & Contractual Due Diligence in ${city}`,
          paragraphs: [
            `Before signing contracts or transferring advance payments in ${city}, ensure all deliverables, timelines, warranty clauses, and cancellation terms are documented in official invoices.`
          ]
        }
      ];
      faqs = [
        {
          question: `What regulatory checks should I perform before choosing ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Verify official statutory registration credentials (${regTerm}) and valid GSTIN tax certificates.`
        },
        {
          question: `How can I contact verified providers of ${categoryPlural.toLowerCase()} in ${city}?`,
          answer: `Use the direct call and WhatsApp links on Zha Search to connect directly with business managers.`
        },
        {
          question: `Are pricing estimates provided free of charge in ${city}?`,
          answer: `Yes, most verified providers on Zha Search offer initial phone consultations and preliminary estimates at zero charge.`
        }
      ];
      break;
  }

  return {
    h1Title,
    h2Subheading,
    introParagraph,
    sections,
    faqs,
    pricingTable: getCategoryPricingTable(categoryRaw, categoryPlural, city),
    statutoryNotice
  };
}

export function getFaqJsonLdSchema(faqs: FaqItem[]): string {
  const schemaObj = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer
      }
    }))
  };
  return JSON.stringify(schemaObj);
}
